import React, { useMemo } from 'react';
import SettingsSurface from './settings-surface';
import ModalHost from './modal-host';
import fastFillSettingsLayout, {
  createEmptyFastFillConfiguration,
} from './fast-fill-settings-layout';
import fastFillTargetFieldsLayout from './fast-fill-target-fields-layout';
import {
  cloneFastFillSettings,
  commitFastFillSettings,
  hasSubmittableFastFillSettings,
  readFastFillEnvironment,
  readFastFillSettings,
  fastFillSettingsAreDirty,
  targetFieldsEnvironmentForConfiguration,
  targetSelectionForConfiguration,
  type FastFillSettingsDraft,
  type FastFillSettingsEnvironment,
  type FastFillTargetSelection,
} from './fast-fill-settings-adapter';

interface FastFillSettingsProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

interface FastFillTargetFieldsContext {
  readonly draft: FastFillSettingsDraft;
  readonly rowId?: string;
}

const fastFillTargetSelectionIsDirty = (
  initial: FastFillTargetSelection,
  draft: FastFillTargetSelection,
): boolean => (
  initial.configurationId !== draft.configurationId
  || initial.selectedTargetFieldKeys.length !== draft.selectedTargetFieldKeys.length
  || initial.selectedTargetFieldKeys.some(
    (value, index) => value !== draft.selectedTargetFieldKeys[index],
  )
  || (initial.selectedLegacyTargetDataNames ?? []).length
    !== (draft.selectedLegacyTargetDataNames ?? []).length
  || (initial.selectedLegacyTargetDataNames ?? []).some(
    (value, index) => value !== draft.selectedLegacyTargetDataNames?.[index],
  )
);

const sourceFieldForTargetFields = (
  { draft, rowId }: FastFillTargetFieldsContext,
  environment: FastFillSettingsEnvironment,
) => targetFieldsEnvironmentForConfiguration(draft, environment, rowId).sourceField;

export default function FastFillSettings({
  open,
  onClose,
}: FastFillSettingsProps) {
  const initialData = useMemo(() => {
    const settings = readFastFillSettings();

    return !open || settings.configurations.length > 0
      ? settings
      : {
        ...settings,
        configurations: [createEmptyFastFillConfiguration()],
      };
  }, [open]);
  const environment = useMemo(readFastFillEnvironment, [open]);
  const child = useMemo(() => ({
    title: (context: FastFillTargetFieldsContext) => {
      const sourceField = sourceFieldForTargetFields(context, environment);

      return sourceField ? `Select Target Fields for ${sourceField.label}` : 'Select Target Fields';
    },
    subtitle: 'Choose which fields should be filled by Photo FastFill.',
    getInitialData: ({ draft, rowId }: FastFillTargetFieldsContext) => (
      targetSelectionForConfiguration(draft, rowId)
    ),
    cloneDraft: (draft: FastFillTargetSelection): FastFillTargetSelection => ({
      configurationId: draft.configurationId,
      selectedTargetFieldKeys: [...draft.selectedTargetFieldKeys],
      ...(draft.selectedLegacyTargetDataNames
        ? { selectedLegacyTargetDataNames: [...draft.selectedLegacyTargetDataNames] }
        : {}),
      ...(draft.legacyPromptOrder
        ? { legacyPromptOrder: draft.legacyPromptOrder.map((target) => ({ ...target })) }
        : {}),
    }),
    environment: ({ draft, rowId }: FastFillTargetFieldsContext) => (
      targetFieldsEnvironmentForConfiguration(draft, environment, rowId)
    ),
    layout: fastFillTargetFieldsLayout,
    host: ModalHost,
    submitLabel: 'Apply',
    cancelLabel: 'Cancel',
    hideCloseButton: true,
    paperSx: (theme) => ({
      overflow: 'hidden',
      width: { md: theme.spacing(78.75) },
      '& .MuiDialogTitle-root': {
        overflow: 'hidden',
        padding: theme.spacing(2, 3, 0.5),
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiDialogContent-root': { overflow: 'hidden', padding: 0 },
      '& .MuiDialogActions-root': { padding: theme.spacing(1, 3) },
    }),
    submitDisabled: (initial: FastFillTargetSelection, draft: FastFillTargetSelection) => (
      !fastFillTargetSelectionIsDirty(initial, draft)
    ),
    isDirty: fastFillTargetSelectionIsDirty,
    confirmDiscard: true,
    ignoreBackdropClick: false,
    apply: (
      draft: FastFillSettingsDraft,
      selection: FastFillTargetSelection,
      { rowId }: { rowId?: string },
    ): FastFillSettingsDraft => {
      if (!rowId) return draft;

      const hasConfiguration = draft.configurations.some(({ id }) => id === rowId);
      if (!hasConfiguration) {
        return draft;
      }

      return {
        ...draft,
        configurations: draft.configurations.map((currentConfiguration) => (
          currentConfiguration.id === rowId
            ? {
              ...currentConfiguration,
              targetFieldKeys: [...selection.selectedTargetFieldKeys],
              legacyTargetDataNames: [...(selection.selectedLegacyTargetDataNames ?? [])],
              legacyPromptOrder: selection.legacyPromptOrder?.map((target) => ({ ...target })) ?? [],
            }
            : currentConfiguration
        )),
      };
    },
  }), [environment]);

  return (
    <SettingsSurface
      open={open}
      title="FastFill Settings"
      initialData={initialData}
      targetIdentity={window.FormBuilder?.model}
      cloneDraft={cloneFastFillSettings}
      environment={environment}
      layout={fastFillSettingsLayout}
      host={ModalHost}
      submitLabel="Save"
      cancelLabel="Cancel"
      formSx={{ margin: 0 }}
      submitDisabled={(draft) => (
        !fastFillSettingsAreDirty(initialData, draft)
        || !hasSubmittableFastFillSettings(initialData, draft)
      )}
      child={child}
      isDirty={fastFillSettingsAreDirty}
      onSubmit={commitFastFillSettings}
      onClose={onClose}
    />
  );
}
